import { NO_PROXY } from '@hit-game/shared/constants';
import type { IProxy } from '@hit-game/shared/types';
import { Check, Copy, Eye, EyeOff, RefreshCw, Shield, Trash2, X } from 'lucide-react';
import { useState } from 'react';

import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { formatDate } from '../../utils/format';
import type { AccountWithSecret } from '../../services/account.service';
import { Dropdown } from '../common/Dropdown';

const ICON_BUTTON = 'rounded p-1 text-ink-dim transition-colors hover:bg-panel-hover';

interface AccountRowProps {
  account: AccountWithSecret;
  proxies: IProxy[];
  onAssignProxy: (accountId: string, proxy: string) => Promise<boolean>;
  onRefreshSession: (accountId: string) => Promise<void>;
  onDelete: (account: AccountWithSecret) => void;
}

export function AccountRow({
  account,
  proxies,
  onAssignProxy,
  onRefreshSession,
  onDelete,
}: AccountRowProps) {
  const [editingProxy, setEditingProxy] = useState(false);
  // Chú kiểu tường minh: `NO_PROXY` là literal `as const`, để suy kiểu thì state
  // bị thu hẹp về đúng chuỗi đó và không nhận nổi giá trị proxy nào khác.
  const [proxyValue, setProxyValue] = useState<string>(NO_PROXY);
  const [showPassword, setShowPassword] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, copy] = useCopyToClipboard();

  const accountId = String(account._id);

  // Proxy rảnh + proxy đang gán cho chính tài khoản này.
  const proxyOptions = [
    { value: NO_PROXY, label: NO_PROXY },
    ...proxies
      .filter((item) => !item.accountId || String(item.accountId) === accountId)
      .map((item) => ({ value: item.value, label: item.value })),
  ];

  const assignedProxy = proxies.find((item) => String(item.accountId) === accountId);

  const handleSaveProxy = async () => {
    if (await onAssignProxy(accountId, proxyValue)) setEditingProxy(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefreshSession(accountId);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <tr className="border-panel-hover hover:bg-panel-raised border-t transition-colors">
      <td className="text-ink px-5 py-3 font-mono text-[12px] font-bold">{account.username}</td>

      <td className="text-ink-muted px-5 py-3 text-[12px]">{account.fullname ?? '-'}</td>

      <td className="px-5 py-3">
        {account.password ? (
          <div className="flex items-center gap-1.5">
            <span className="text-ink font-mono text-[12px]">
              {showPassword ? account.password : '•'.repeat(account.password.length)}
            </span>
            <button
              type="button"
              onClick={() => {
                setShowPassword((v) => !v);
              }}
              className={`${ICON_BUTTON} hover:text-ink`}
              title={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
            >
              {showPassword ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
            <button
              type="button"
              onClick={() => {
                copy(account.password ?? '');
              }}
              className={`${ICON_BUTTON} hover:text-accent`}
              title="Sao chép mật khẩu"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
        ) : (
          <span className="text-ink-faint text-[12px]">-</span>
        )}
      </td>

      <td className="px-5 py-3">
        {editingProxy ? (
          <div className="flex items-center gap-1.5">
            <Dropdown
              value={proxyValue}
              onChange={setProxyValue}
              placeholder="-- Chọn proxy --"
              className="min-w-[200px]"
              options={proxyOptions}
            />
            <button
              type="button"
              onClick={() => {
                void handleSaveProxy();
              }}
              className="bg-accent hover:bg-accent-hover rounded px-2 py-1.5 text-[10px] font-bold text-white"
              aria-label="Lưu proxy"
            >
              <Check size={12} />
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingProxy(false);
              }}
              className="bg-line text-ink-muted hover:bg-panel-hover rounded px-2 py-1.5 text-[10px] font-bold"
              aria-label="Huỷ"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <Shield size={12} className="text-ink-ghost" />
            <span className="text-ink-muted font-mono text-[11px]">
              {assignedProxy ? assignedProxy.value.split(':').slice(0, 2).join(':') : '-'}
            </span>
          </div>
        )}
      </td>

      <td className="text-ink-dim px-5 py-3 text-[12px]">{formatDate(account.createdAt)}</td>

      <td className="px-5 py-3 text-center">
        {!editingProxy && (
          <div className="flex items-center justify-center gap-1">
            <button
              type="button"
              onClick={() => {
                setProxyValue(assignedProxy?.value ?? NO_PROXY);
                setEditingProxy(true);
              }}
              className={`${ICON_BUTTON} hover:text-accent`}
              title="Đổi proxy"
            >
              <Shield size={14} />
            </button>
            <button
              type="button"
              onClick={() => {
                void handleRefresh();
              }}
              disabled={refreshing}
              className={`${ICON_BUTTON} hover:text-success disabled:opacity-40`}
              title="Đăng nhập lại để làm mới phiên"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={() => {
                onDelete(account);
              }}
              className={`${ICON_BUTTON} hover:text-danger`}
              title="Xoá tài khoản"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
